import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DarkPage } from "@/components/app/PageHeader";
import { ChevronLeft, ChevronRight, Users, Inbox } from "lucide-react";

const PRIORITY_DOT = { critical: "bg-rose-400", high: "bg-amber-400", medium: "bg-[#00F5D4]", low: "bg-[#7d8590]" };

function startOfWeek(d) {
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day; // Monday
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

const TaskCard = ({ t }) => (
  <Link to={`/workspace/${t.project_id}`} className="block bg-[#161b22] border border-[#30363d] rounded-md px-2 py-1.5 mb-1.5 hover:border-[#00F5D4]/40 transition-colors">
    <div className="flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${PRIORITY_DOT[t.priority] || PRIORITY_DOT.medium}`} />
      <span className="text-xs text-white truncate">{t.title}</span>
    </div>
    <div className="text-[10px] text-[#7d8590] truncate mt-0.5">{t.project_name}</div>
  </Link>
);

export default function Planner() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (ws) => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/planner/week?week_start=${ws.toISOString().slice(0, 10)}`);
      setData(d);
    } catch {
      toast.error("Failed to load planner");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(weekStart); }, [load, weekStart]);

  const shiftWeek = (delta) => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + delta * 7);
    setWeekStart(next);
  };

  const members = data?.members || [];

  return (
    <DarkPage><div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Planner</h1>
          <p className="text-sm text-[#7d8590] mt-1">Who has what scheduled this week, across every project — plus each person's unscheduled backlog.</p>
        </div>
        <div className="flex items-center gap-2 bg-[#0d1117] border border-[#21262d] rounded-lg px-2 py-1.5">
          <button onClick={() => shiftWeek(-1)} className="p-1 text-[#7d8590] hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-xs text-white font-mono min-w-[150px] text-center">
            {data ? `${data.week_start} → ${data.week_end}` : "…"}
          </span>
          <button onClick={() => shiftWeek(1)} className="p-1 text-[#7d8590] hover:text-white"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={() => setWeekStart(startOfWeek(new Date()))} className="text-xs text-[#00F5D4] hover:underline ml-2">Today</button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-[#7d8590] text-sm">Loading…</div>
      ) : members.length === 0 ? (
        <div className="text-center py-20 text-[#7d8590] text-sm">
          <Users className="w-8 h-8 mx-auto mb-2 text-[#30363d]" />
          Nobody has open tasks scheduled or backlogged this week.
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#21262d] rounded-lg">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#0d1117] border-b border-[#21262d]">
                <th className="text-left px-4 py-2.5 text-xs text-[#7d8590] font-medium sticky left-0 bg-[#0d1117] min-w-[140px]">Person</th>
                {(members[0]?.days || []).map(d => (
                  <th key={d.date} className="text-left px-3 py-2.5 text-xs text-[#7d8590] font-medium min-w-[160px]">{d.label} <span className="text-[#4B5563] font-mono">{d.date.slice(5)}</span></th>
                ))}
                <th className="text-left px-3 py-2.5 text-xs text-[#7d8590] font-medium min-w-[160px]">
                  <span className="flex items-center gap-1"><Inbox className="w-3 h-3" /> Backlog</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.user_id} className="border-b border-[#21262d] last:border-0 align-top">
                  <td className="px-4 py-3 text-white font-medium sticky left-0 bg-[#0d1117]">{m.name}</td>
                  {m.days.map(d => (
                    <td key={d.date} className="px-3 py-3">
                      {d.tasks.map(t => <TaskCard key={t.task_id} t={t} />)}
                    </td>
                  ))}
                  <td className="px-3 py-3">
                    {m.backlog.map(t => <TaskCard key={t.task_id} t={t} />)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div></DarkPage>
  );
}
